import { Resource } from '@modules/admin-generic/admin-generic.types'
import {
  getResourceConfig,
  registerInputRefFactory,
} from '@modules/admin-generic/admin-generic.utils'
import {
  Autocomplete,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from '@mui/material'
import makeStyles from '@mui/styles/makeStyles'
import { BackdropProgress } from '@shared/components/backdrop-progress.component'
import { ErrorsAlert } from '@shared/components/errors-alert.component'
import { ProgressButton } from '@shared/components/progress-button.component'
import { TextField } from '@shared/components/text-field.component'
import { NO_OPTIONS_PLACEHOLDER } from '@shared/consts'
import { useHandleResourceState } from '@shared/hooks/use-handle-resource-state.hook'
import { useRouter } from '@shared/hooks/use-router'
import { useThemeStyles } from '@shared/theme/use-theme-styles'
import {
  AdminResourceType,
  NewsletterExternalModule,
  Scope,
} from '@shared/types'
import { apiJSON } from '@shared/utils/api/api.util'
import { isEqual } from 'lodash'
import React, { useState } from 'react'
import { Controller, FormProvider } from 'react-hook-form'
import { useQuery } from 'react-query'
import { Navigate } from 'react-router-dom'

const newsletterExternalModulesEndpoint = 'newsletters/modules'

export const ScopesCrudDialog = () => {
  const classes = useStyles()
  const sharedClasses = useThemeStyles()

  const { navigate, query } = useRouter<{ resourceID?: string }>()

  const {
    data: newsletterExternalModules,
    isLoading: isLoadingNewsletterExternalModules,
  } = useQuery<NewsletterExternalModule[]>(
    newsletterExternalModulesEndpoint,
    () => apiJSON(newsletterExternalModulesEndpoint)
  )

  const resourceConfig =
    getResourceConfig(AdminResourceType.SCOPES) ?? ({} as Resource)
  const {
    dto: resourceSchemaKey,
    singular: resourceNameSingular,
    endpoint,
  } = resourceConfig

  const {
    formMethods,
    onAddResource,
    mutation: {
      isResourceEdit,
      isResourceCreating,
      isResourceCreationSucceed,
      createResourceErrors,
      resetResourceCreationState,
    },
  } = useHandleResourceState<Scope>({
    resourceEndpoint: endpoint,
    resourceID: query?.resourceID,
  })

  const registerInputRef = registerInputRefFactory(formMethods.register)

  if (isResourceCreationSucceed) {
    return <Navigate to="/admin/scopes" />
  }

  return !isLoadingNewsletterExternalModules ? (
    <Dialog open fullWidth maxWidth="md">
      <form noValidate>
        <FormProvider {...formMethods}>
          <DialogTitle className={sharedClasses.dialogTitle}>
            {isResourceEdit ? 'Edytuj' : 'Utwórz nowy'}
          </DialogTitle>
          <DialogContent dividers>
            <ErrorsAlert
              errors={createResourceErrors}
              onClose={resetResourceCreationState}
            />
            <TextField
              {...registerInputRef('order', { required: true })}
              required
              label="Pozycja"
            />
            <TextField
              {...registerInputRef('name', { required: true })}
              required
              label="Nazwa zakresu"
            />
            <TextField
              {...registerInputRef('imageURL', { required: true })}
              required
              label="Link do ikonki"
            />
            <Controller
              name="newsletterExternalModules"
              control={formMethods.control}
              render={(formControllerProps) => (
                <Autocomplete
                  value={formControllerProps.field.value || []}
                  autoHighlight
                  multiple
                  options={newsletterExternalModules || []}
                  getOptionLabel={(option: NewsletterExternalModule) =>
                    option ? `${option.shortcut} - ${option.fullName}` : ''
                  }
                  isOptionEqualToValue={isEqual}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Moduły zewnętrzne (newslettery)"
                    />
                  )}
                  onChange={(_e, data) =>
                    formControllerProps.field.onChange(data)
                  }
                  filterSelectedOptions
                  noOptionsText={NO_OPTIONS_PLACEHOLDER}
                />
              )}
              defaultValue={[]}
            />
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => navigate(-1)}
              color="primary"
              variant="outlined"
            >
              Anuluj
            </Button>
            <ProgressButton
              isLoading={isResourceCreating}
              onClick={() => onAddResource()}
              color="primary"
              variant="contained"
            >
              Zapisz
            </ProgressButton>
          </DialogActions>
        </FormProvider>
      </form>
    </Dialog>
  ) : (
    <BackdropProgress />
  )
}

const useStyles = makeStyles(() => {
  return {
    saveMethodRadio: {
      display: 'none',
    },
    addLegalBasis: {
      position: 'absolute',
      top: 0,
      right: 10,
    },
    removeLegalBasis: {
      position: 'absolute',
      right: -10,
      top: -10,
    },
    legalBasisList: {
      marginTop: 15,
      padding: '16px 0px 10px 16px',
      position: 'relative',
    },
    legalBasisItem: {
      position: 'relative',
      marginBottom: 15,
    },
    isPublishedLabel: {
      flex: 1,
      marginLeft: 5,
    },
  }
})
